import {UserModel} from '../auth/models/UserModel'
import {FreshDeskTicketReporterOption} from './api'

export type AssignedReporterOption = {
  value: string
  label: string
  email: string
  fullName: string
}

export const isAdministratorUser = (user?: Pick<UserModel, 'userRoles'>) => {
  return (user?.userRoles ?? []).some((role) => role.trim().toLowerCase() === 'administrator')
}

export const buildReporterLabel = (fullName: string, email: string) => {
  const normalizedFullName = fullName.trim()
  const normalizedEmail = email.trim()

  if (!normalizedFullName || normalizedFullName.toLowerCase() === normalizedEmail.toLowerCase()) {
    return normalizedEmail
  }

  if (!normalizedEmail) {
    return normalizedFullName
  }

  return `${normalizedFullName} - ${normalizedEmail}`
}

export const getCurrentUserReporterOption = (user?: UserModel): AssignedReporterOption | null => {
  const email = (user?.email || user?.username || '').trim()
  if (!email) {
    return null
  }

  const fullName =
    `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ||
    user?.fullname ||
    email

  return {
    value: email,
    label: buildReporterLabel(fullName, email),
    email,
    fullName,
  }
}

export const toAssignedReporterOption = (
  reporter: FreshDeskTicketReporterOption
): AssignedReporterOption | null => {
  const email = reporter.email?.trim() ?? ''
  if (!email) {
    return null
  }

  const fullName = reporter.fullName?.trim() || email

  return {
    value: email,
    label: buildReporterLabel(fullName, email),
    email,
    fullName,
  }
}

export const buildAssignedReporterOptions = (
  reporters: FreshDeskTicketReporterOption[],
  currentUser?: UserModel
) => {
  const options = reporters
    .map(toAssignedReporterOption)
    .filter((option): option is AssignedReporterOption => option !== null)

  const currentUserOption = getCurrentUserReporterOption(currentUser)
  if (currentUserOption && !options.some((option) => option.value.toLowerCase() === currentUserOption.value.toLowerCase())) {
    return [currentUserOption, ...options]
  }

  return options
}

export const getAssignedReporterEmail = (selectedEmail: string, currentUser?: UserModel) => {
  return selectedEmail.trim() || getCurrentUserReporterOption(currentUser)?.email || ''
}
